import React from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Report as ReportType } from 'donor/portal/pages/DonorReceipt/utils';
import {
  extractNameFromJSONObject,
  toTitleCase,
} from 'shared/utils/stringUtils';
import { ReportsTable } from 'npo/pages/ReportsBeta/ReportGroup';
import { useReport } from 'shared/providers/ReportProvider';
import { mapCustomQuestionResponses } from './utils';
import { RenderHtml } from 'npo/pages/ReportBuilder/Components/Review/ReportCustomQuestionType/utils';
import { StyledReportCustomQuestions } from '../ReportCustomQuestions';
import { renderTextWithParagraphs } from '../ReportNarrative';
import { mergeKeyNames } from 'shared/utils/mergeClassNames';

const DEFAULT_NOT_ANSWERED_TEXT = 'Not answered';

const customQuestionOptions = {
  hideUnanswered: false,
  notAnsweredText: DEFAULT_NOT_ANSWERED_TEXT,
};

interface LegacyReportInfoProps {
  report?: ReportType | ReportsTable;
  showInterventionCategories?: boolean;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  padding-top: ${SPACERS.large};
  padding-bottom: ${SPACERS.large};
  border-top: 1px solid ${COLORS.grayLight};
  .report-budget-header {
    padding-left: ${SPACERS.extraLarge};
    padding-right: ${SPACERS.extraLarge};
    padding-bottom: ${SPACERS.tiny};
  }
  .report-total-budget {
    display: flex;
    color: ${COLORS.whiteBase};
    justify-content: space-between;
    background-color: ${COLORS.grayDarkest};
    border-top: 1px solid ${COLORS.grayLight};
    padding: ${SPACERS.medium} ${SPACERS.extraLarge};
    .report-total-budget-amount {
      letter-spacing: 1px;
    }
  }
`;

const RowDiv = styled.div`
  display: grid;
  gap: ${SPACERS.small};
  align-items: center;
  grid-template-columns: 1fr 3fr;
  padding: ${SPACERS.small} ${SPACERS.extraLarge};
  border-top: 1px solid ${COLORS.grayLight};
`;

const LegacyReportInfo: React.FC<LegacyReportInfoProps> = ({
  report,
  showInterventionCategories = true,
}) => {
  const { report: reportFromProvider } = useReport();
  const customQuestionsSchema =
    reportFromProvider.data?.allocations?.[0]?.investment?.portfolio
      ?.customQuestions;

  // Helper to convert various possible value types to a printable string
  type GraphQLObject = {
    values?: unknown[];
    [key: string]: unknown;
  };

  const formatValue = (val: unknown): string => {
    if (
      val === null ||
      val === undefined ||
      val === '' ||
      (typeof val === 'object' &&
        Object.keys(val).length === 0 &&
        val.constructor === Object)
    ) {
      return DEFAULT_NOT_ANSWERED_TEXT;
    }

    if (Array.isArray(val)) {
      return val.length
        ? val.map(String).join(', ')
        : DEFAULT_NOT_ANSWERED_TEXT;
    }

    // If the value is a JSON-shaped string, parse it and recurse so nested
    // shapes like `{ values: [...] }` go through the object handler below.
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return formatValue(JSON.parse(trimmed));
        } catch {
          // not valid JSON — fall through and return as-is
        }
      }
    }

    if (typeof val === 'object') {
      const obj = val as GraphQLObject;

      // Handle GraphQL pattern: { values: [...] }
      if (Array.isArray(obj.values)) {
        const formattedValues = obj.values
          .map(item => {
            if (item === null || item === undefined) return undefined;
            if (typeof item === 'object' && item !== null) {
              const candidate = item as Record<string, unknown>;
              return (
                (candidate['Subject'] as string) ||
                (candidate['name'] as string) ||
                (candidate['description'] as string) ||
                (candidate['value'] as string) ||
                JSON.stringify(candidate)
              );
            }
            return String(item);
          })
          .filter(Boolean)
          .join(', ');

        return formattedValues || DEFAULT_NOT_ANSWERED_TEXT;
      }

      // Try to extract name from JSON object (for Primary Intervention Type and similar fields)
      const extractedName = extractNameFromJSONObject(obj);
      if (extractedName) {
        return extractedName;
      }

      return JSON.stringify(obj);
    }

    return String(val);
  };
  const categories = [
    // hidden as they are now in the narrative section
    /*{
      title: 'Success Story',
      value: formatValue(report?.successStory)
    },
    {
      title: 'Lesson Learned',
      value: formatValue(report?.lessonLearned)
    },*/
    {
      title: 'Intervention Dosage Level',
      value: formatValue(report?.interventionDosageLevel),
    },
    {
      title: 'Intervention Dosage Number',
      value: formatValue(report?.interventionDosageNumber),
    },
    {
      title: 'Intervention Dosage Type',
      value: formatValue(report?.interventionDosageType),
    },
    {
      title: 'Primary Intervention Description',
      value: formatValue(report?.primaryInterventionDescription),
    },
    {
      title: 'Primary Intervention Subtypes',
      value: formatValue(report?.primaryInterventionSubtypes),
    },
    {
      title: 'Primary Intervention Type',
      value: formatValue(report?.primaryInterventionType),
    },
  ];

  const mappedQuestions = mapCustomQuestionResponses(
    customQuestionsSchema,
    report?.customQuestionResponses,
    customQuestionOptions,
  );
  const hasQuestions = mappedQuestions.length > 0;

  const parseCategoryValue = (value: string): string => {
    // If value is already processed and doesn't look like JSON, return as is
    if (!value || value === DEFAULT_NOT_ANSWERED_TEXT || !value.includes('{')) {
      return value;
    }

    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) {
        const extractedName = extractNameFromJSONObject(parsed);
        if (extractedName) {
          return extractedName;
        }

        // Additional fallback for title field
        if (typeof parsed.title === 'string') {
          return toTitleCase(parsed.title);
        }
      }
      return value;
    } catch {
      // If it's not valid JSON, return the original value
      return value;
    }
  };

  return (
    <>
      {hasQuestions && (
        <StyledReportCustomQuestions>
          <TrueImpactText variant="heading-label">Report Survey</TrueImpactText>
          {mappedQuestions.map((q, index) => (
            <div
              key={mergeKeyNames(q.name, index)}
              className="report-custom-question"
            >
              <TrueImpactText
                variant="body-small-bold"
                className="report-custom-question-text"
              >
                <RenderHtml text={q.title} />
              </TrueImpactText>
              <TrueImpactText variant="body-small">{q.response}</TrueImpactText>
            </div>
          ))}
        </StyledReportCustomQuestions>
      )}
      {showInterventionCategories && (
        <StyledDiv>
          <TrueImpactText
            variant="heading-subheading"
            className="report-budget-header"
          >
            Report Activities
          </TrueImpactText>
          {categories.map((category, index) => (
            <RowDiv key={index}>
              <TrueImpactText variant="body-small-bold">
                {category.title}
              </TrueImpactText>
              <TrueImpactText variant="body-small">
                {renderTextWithParagraphs(parseCategoryValue(category.value))}
              </TrueImpactText>
            </RowDiv>
          ))}
        </StyledDiv>
      )}
    </>
  );
};

export default LegacyReportInfo;
